// Typed wrappers around backend endpoints.

import { ApiError, apiFetch, rawAuthedFetch } from "@/api/client";
import { setAuth, clearAuth } from "@/api/auth-store";
import { queryClient } from "@/lib/queryClient";
import type {
  ActionItemsOut,
  AdminUser,
  AuditLogEntry,
  ContaminantRule,
  CurrentUser,
  DocumentList,
  Invitation,
  InvitationCheck,
  InvitationCreated,
  InvitationStatus,
  MonitoringSchedule,
  NitrateNitriteOverview,
  NitrateNitriteStatus,
  OnboardingRequest,
  OnboardingResult,
  OverrideWrite,
  PwsLookup,
  Role,
  RuleWrite,
  Scope,
  SmtpSecurity,
  SmtpSettings,
  SmtpTestResult,
  SystemAttributes,
  SystemAttributesWrite,
  SystemData,
  SystemEnriched,
  SystemSummary,
  Tenant,
  TenantOverview,
  TokenResponse,
  Waiver,
  WaiverWrite,
} from "@/types/api";

export async function login(email: string, password: string): Promise<TokenResponse> {
  const body = await apiFetch<TokenResponse>("/api/v1/auth/login", {
    method: "POST",
    body: { email, password },
  });
  setAuth({
    accessToken: body.access_token,
    scope: body.scope,
    role: body.role,
    tenantId: body.tenant_id,
  });
  // A different account may sign in on this browser — cached queries must
  // not cross the auth boundary.
  queryClient.clear();
  return body;
}

export async function logout(): Promise<void> {
  try {
    await apiFetch<void>("/api/v1/auth/logout", { method: "POST" });
  } catch {
    // Best-effort: the server-side token may already be gone; local state
    // is cleared regardless and callers must still navigate away.
  } finally {
    clearAuth();
    queryClient.clear();
  }
}

export async function forgotPassword(email: string): Promise<void> {
  await apiFetch<{ detail: string }>("/api/v1/auth/forgot-password", {
    method: "POST",
    body: { email },
  });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await apiFetch<void>("/api/v1/auth/reset-password", {
    method: "POST",
    body: { token, password },
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiFetch<void>("/api/v1/auth/change-password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

export async function fetchMe(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/api/v1/users/me");
}

export async function updateMyPreferences(prefs: {
  notify_email?: boolean;
}): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/api/v1/users/me", { method: "PATCH", body: prefs });
}

export async function listUsers(tenantId?: string): Promise<AdminUser[]> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  return apiFetch<AdminUser[]>(`/api/v1/users${qs}`);
}

export async function setUserActive(userId: string, isActive: boolean): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/api/v1/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: { is_active: isActive },
  });
}

export async function resendInvitation(id: string): Promise<InvitationCreated> {
  return apiFetch<InvitationCreated>(
    `/api/v1/invitations/${encodeURIComponent(id)}/resend`,
    { method: "POST" },
  );
}

export async function fetchWaivers(pwsId: string): Promise<Waiver[]> {
  return apiFetch<Waiver[]>(`/api/v1/systems/${encodeURIComponent(pwsId)}/waivers`);
}

export async function createWaiver(pwsId: string, body: WaiverWrite): Promise<Waiver> {
  return apiFetch<Waiver>(`/api/v1/systems/${encodeURIComponent(pwsId)}/waivers`, {
    method: "POST",
    body,
  });
}

export async function deleteWaiver(pwsId: string, waiverId: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/waivers/${encodeURIComponent(waiverId)}`,
    { method: "DELETE" },
  );
}

export async function fetchTenantsOverview(): Promise<TenantOverview[]> {
  return apiFetch<TenantOverview[]>("/api/v1/tenants/overview");
}

export async function removeSystemFromTenant(
  tenantId: string,
  pwsId: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/systems/${encodeURIComponent(pwsId)}`,
    { method: "DELETE" },
  );
}

export async function moveSystemToTenant(
  tenantId: string,
  pwsId: string,
  targetTenantId: string,
): Promise<void> {
  await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/systems/${encodeURIComponent(pwsId)}/move`,
    { method: "POST", body: { target_tenant_id: targetTenantId } },
  );
}

export async function fetchActionItems(tenantId?: string): Promise<ActionItemsOut> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  return apiFetch<ActionItemsOut>(`/api/v1/systems/action-items${qs}`);
}

// Cross-tenant action queue for platform staff — served from the nightly
// snapshot (see backend app/cli/jobs.run_snapshot), so it carries an as_of.
export async function fetchPlatformActionItems(): Promise<ActionItemsOut> {
  return apiFetch<ActionItemsOut>("/api/v1/platform/action-items");
}

export async function fetchSystems(): Promise<SystemSummary[]> {
  return apiFetch<SystemSummary[]>("/api/v1/systems");
}

export async function fetchSystemsSummary(): Promise<SystemEnriched[]> {
  return apiFetch<SystemEnriched[]>("/api/v1/systems/summary");
}

export async function fetchSystem(pwsId: string): Promise<SystemData> {
  return apiFetch<SystemData>(`/api/v1/systems/${encodeURIComponent(pwsId)}`);
}

export async function refreshSystem(pwsId: string): Promise<SystemData> {
  return apiFetch<SystemData>(`/api/v1/systems/${encodeURIComponent(pwsId)}/refresh`, {
    method: "POST",
  });
}

export async function fetchSystemDocuments(pwsId: string): Promise<DocumentList> {
  return apiFetch<DocumentList>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/documents`,
  );
}

export async function refreshSystemDocuments(pwsId: string): Promise<DocumentList> {
  return apiFetch<DocumentList>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/documents/refresh`,
    { method: "POST" },
  );
}

export async function fetchNitrateNitriteCompliance(
  pwsId: string,
): Promise<NitrateNitriteStatus> {
  return apiFetch<NitrateNitriteStatus>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/compliance/nitrate-nitrite`,
  );
}

export async function fetchNitrateNitriteOverview(): Promise<NitrateNitriteOverview> {
  return apiFetch<NitrateNitriteOverview>("/api/v1/compliance/nitrate-nitrite");
}

export async function fetchTenants(): Promise<Tenant[]> {
  return apiFetch<Tenant[]>("/api/v1/tenants");
}

// Audit log (platform-only) --------------------------------------------

export interface AuditFilters {
  actor?: string;
  action?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;
}

function auditQuery(filters: AuditFilters): string {
  const p = new URLSearchParams();
  if (filters.actor) p.set("actor", filters.actor);
  if (filters.action) p.set("action", filters.action);
  if (filters.dateFrom) p.set("date_from", filters.dateFrom);
  if (filters.dateTo) p.set("date_to", filters.dateTo);
  return p.toString();
}

export async function fetchAuditLog(
  limit = 100,
  offset = 0,
  filters: AuditFilters = {},
): Promise<AuditLogEntry[]> {
  const qs = auditQuery(filters);
  return apiFetch<AuditLogEntry[]>(
    `/api/v1/audit-log?limit=${limit}&offset=${offset}${qs ? `&${qs}` : ""}`,
  );
}

export async function fetchAuditActions(): Promise<string[]> {
  return apiFetch<string[]>("/api/v1/audit-log/actions");
}

export function auditExportUrl(filters: AuditFilters): string {
  const qs = auditQuery(filters);
  return `/api/v1/audit-log/export.csv${qs ? `?${qs}` : ""}`;
}

export async function downloadAuditCsv(filters: AuditFilters): Promise<void> {
  // Manual fetch: apiFetch JSON-parses, and the export needs the bearer
  // header, so a plain <a href> won't authenticate. rawAuthedFetch shares
  // apiFetch's 401 -> refresh -> retry path so an expired access token
  // doesn't fail the export.
  const res = await rawAuthedFetch(auditExportUrl(filters));
  if (!res.ok) throw new ApiError(res.status, "Export failed", null);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "aquatrack-activity.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// PWS onboarding --------------------------------------------------------

export async function lookupPws(pwsId: string): Promise<PwsLookup> {
  return apiFetch<PwsLookup>(
    `/api/v1/onboarding/lookup/${encodeURIComponent(pwsId)}`,
  );
}

export async function onboardPws(input: OnboardingRequest): Promise<OnboardingResult> {
  return apiFetch<OnboardingResult>("/api/v1/onboarding", {
    method: "POST",
    body: input,
  });
}

// Invitations -----------------------------------------------------------

export interface CreateInvitationInput {
  email: string;
  scope: Scope;
  role: Role;
  tenant_id?: string | null;
}

export async function createInvitation(
  input: CreateInvitationInput,
): Promise<InvitationCreated> {
  return apiFetch<InvitationCreated>("/api/v1/invitations", {
    method: "POST",
    body: input,
  });
}

export async function listInvitations(
  status?: InvitationStatus,
): Promise<Invitation[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<Invitation[]>(`/api/v1/invitations${qs}`);
}

export async function revokeInvitation(id: string): Promise<Invitation> {
  return apiFetch<Invitation>(
    `/api/v1/invitations/${encodeURIComponent(id)}/revoke`,
    { method: "POST" },
  );
}

export async function checkInvitation(token: string): Promise<InvitationCheck> {
  return apiFetch<InvitationCheck>(
    `/api/v1/invitations/check?token=${encodeURIComponent(token)}`,
  );
}

export async function acceptInvitation(
  token: string,
  password: string,
): Promise<TokenResponse> {
  const body = await apiFetch<TokenResponse>("/api/v1/invitations/accept", {
    method: "POST",
    body: { token, password },
  });
  setAuth({
    accessToken: body.access_token,
    scope: body.scope,
    role: body.role,
    tenantId: body.tenant_id,
  });
  return body;
}

// SMTP settings ---------------------------------------------------------

export interface SmtpUpsertInput {
  host: string;
  port: number;
  username: string | null;
  password: string | null; // null = keep current; "" = clear
  from_email: string;
  from_name: string | null;
  security: SmtpSecurity;
}

export async function getSmtpSettings(): Promise<SmtpSettings | null> {
  return apiFetch<SmtpSettings | null>("/api/v1/smtp-settings");
}

export async function upsertSmtpSettings(
  input: SmtpUpsertInput,
): Promise<SmtpSettings> {
  return apiFetch<SmtpSettings>("/api/v1/smtp-settings", {
    method: "PUT",
    body: input,
  });
}

export async function sendSmtpTest(to: string): Promise<SmtpTestResult> {
  return apiFetch<SmtpTestResult>("/api/v1/smtp-settings/test", {
    method: "POST",
    body: { to },
  });
}

// Regulatory Rule Library -----------------------------------------------

export async function listRules(): Promise<ContaminantRule[]> {
  return apiFetch<ContaminantRule[]>("/api/v1/rules");
}

export async function createRule(input: RuleWrite): Promise<ContaminantRule> {
  return apiFetch<ContaminantRule>("/api/v1/rules", { method: "POST", body: input });
}

export async function updateRule(id: string, input: RuleWrite): Promise<ContaminantRule> {
  return apiFetch<ContaminantRule>(`/api/v1/rules/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: input,
  });
}

export async function deleteRule(id: string): Promise<void> {
  return apiFetch<void>(`/api/v1/rules/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// System attributes -----------------------------------------------------

export async function fetchSystemAttributes(pwsId: string): Promise<SystemAttributes> {
  return apiFetch<SystemAttributes>(`/api/v1/systems/${encodeURIComponent(pwsId)}/attributes`);
}

export async function putSystemAttributes(
  pwsId: string,
  body: SystemAttributesWrite,
): Promise<SystemAttributes> {
  return apiFetch<SystemAttributes>(`/api/v1/systems/${encodeURIComponent(pwsId)}/attributes`, {
    method: "PUT",
    body,
  });
}

export async function fetchMonitoringSchedule(pwsId: string): Promise<MonitoringSchedule> {
  return apiFetch<MonitoringSchedule>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/monitoring-schedule`,
  );
}

export async function setMonitoringOverride(
  pwsId: string,
  ruleId: string,
  body: Partial<OverrideWrite>,
): Promise<void> {
  return apiFetch<void>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/monitoring/${encodeURIComponent(ruleId)}/override`,
    { method: "PUT", body },
  );
}

export async function clearMonitoringOverride(pwsId: string, ruleId: string): Promise<void> {
  return apiFetch<void>(
    `/api/v1/systems/${encodeURIComponent(pwsId)}/monitoring/${encodeURIComponent(ruleId)}/override`,
    { method: "DELETE" },
  );
}
